import axiosInstance from './api';

const getApiErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  return (
    error.response?.data?.message ||
    error.message ||
    error?.error?.message ||
    error?.data?.message ||
    fallback
  );
};

const getAriaEscalationHttpStatus = (error) =>
  error?.response?.status ?? error?.status ?? error?.response?.data?.code ?? null;

/** Parse backend timestamps like `26-08-07 07:20:00` into Date / ISO. */
export const parseAriaEscalationDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const backendMatch = raw.match(/^(\d{2})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  const date = backendMatch
    ? new Date(
        2000 + Number(backendMatch[1]),
        Number(backendMatch[2]) - 1,
        Number(backendMatch[3]),
        Number(backendMatch[4]),
        Number(backendMatch[5]),
        Number(backendMatch[6] ?? 0),
      )
    : new Date(raw);

  return Number.isNaN(date.getTime()) ? null : date;
};

export const toAriaEscalationIso = (value) => {
  const date = parseAriaEscalationDate(value);
  return date ? date.toISOString() : null;
};

const EMPTY_QUEUE_COUNTS = {
  human_requested: 0,
  safety_trust: 0,
  complaint_conflict: 0,
  billing_membership: 0,
  match_dispute: 0,
  technical_support: 0,
  event_complaint: 0,
};

export const normalizeAriaEscalationMember = (member = {}) => ({
  id: member.id ?? null,
  uid: member.uid ?? null,
  email: member.email ?? '',
  fullName: member.fullName ?? member.name ?? member.displayName ?? '',
  avatarUrl: member.avatarUrl ?? member.avatar ?? null,
});

export const normalizeAriaEscalationOwner = (owner) => {
  if (!owner) return null;
  return normalizeAriaEscalationMember(owner);
};

/**
 * Map API escalation status → UI handoff status used by the chat room.
 * open → needs person; in_progress → admin active; resolved stays resolved.
 */
export const mapAriaEscalationApiStatusToUi = (escalation = {}) => {
  const status = String(escalation.status ?? '').toLowerCase();
  if (status === 'resolved') return 'resolved';
  if (status === 'in_progress' || escalation.takenOverAt) return 'admin_active';
  return 'handoff_requested';
};

const SOURCE_PREVIEW_FALLBACK = {
  member_requested: 'Member asked to talk to a person',
  safety_autonomous: 'Aria flagged a safety concern',
};

const normalizeIncident = (incident = {}) => ({
  at: toAriaEscalationIso(incident.at) ?? incident.at ?? null,
  safetyLevel: incident.safetyLevel ?? null,
  reasonSummary:
    incident.reasonSummary != null && String(incident.reasonSummary).trim()
      ? String(incident.reasonSummary).trim()
      : null,
  issueCategory:
    incident.issueCategory != null && String(incident.issueCategory).trim()
      ? String(incident.issueCategory).trim()
      : null,
  keyFacts: Array.isArray(incident.keyFacts)
    ? incident.keyFacts.map((fact) => String(fact).trim()).filter(Boolean)
    : incident.keyFacts != null && String(incident.keyFacts).trim()
      ? [String(incident.keyFacts).trim()]
      : [],
  urgency: incident.urgency ?? null,
  evidenceAvailable: incident.evidenceAvailable ?? null,
});

/**
 * API `summary` may be a plain string (docs) or `{ incidents: [...] }` (live payload).
 */
export const normalizeAriaEscalationSummary = (summary) => {
  if (summary == null) {
    return { text: '', incidents: [], raw: null };
  }

  if (typeof summary === 'string') {
    const text = summary.trim();
    return { text, incidents: [], raw: summary };
  }

  if (typeof summary !== 'object') {
    return { text: '', incidents: [], raw: summary };
  }

  const incidents = Array.isArray(summary.incidents)
    ? summary.incidents.map(normalizeIncident)
    : [];
  const latest = incidents.length > 0 ? incidents[incidents.length - 1] : null;
  const keyFactsText = latest?.keyFacts?.length ? latest.keyFacts.join(', ') : '';
  const text = [latest?.reasonSummary, latest?.issueCategory, keyFactsText]
    .map((part) => (part != null ? String(part).trim() : ''))
    .find(Boolean);

  return {
    text: text || '',
    incidents,
    raw: summary,
  };
};

const buildEscalationPreview = ({ reason, summaryText, source, queue }) => {
  if (reason) return reason;
  if (summaryText) return summaryText;
  if (SOURCE_PREVIEW_FALLBACK[source]) return SOURCE_PREVIEW_FALLBACK[source];
  if (queue) return `Escalation in ${String(queue).replace(/_/g, ' ')}`;
  return 'Escalation opened';
};

/** Normalize one escalation row from GET /aria/escalations. */
export const normalizeAriaEscalation = (escalation = {}) => {
  const member = normalizeAriaEscalationMember(escalation.member);
  const createdAtIso = toAriaEscalationIso(escalation.createdAt);
  const takenOverAtIso = toAriaEscalationIso(escalation.takenOverAt);
  const resolvedAtRaw =
    escalation.resolvedAt != null && String(escalation.resolvedAt).trim()
      ? String(escalation.resolvedAt).trim()
      : null;
  const resolvedAtIso = toAriaEscalationIso(escalation.resolvedAt);
  const slaDueAtIso = toAriaEscalationIso(escalation.slaDueAt);
  const mutedUntilIso = toAriaEscalationIso(escalation.mutedUntil);
  const reason =
    escalation.reason != null && String(escalation.reason).trim()
      ? String(escalation.reason).trim()
      : '';
  const normalizedSummary = normalizeAriaEscalationSummary(escalation.summary);
  const source = escalation.source ?? null;
  const queue = escalation.queue ?? null;
  const preview = buildEscalationPreview({
    reason,
    summaryText: normalizedSummary.text,
    source,
    queue,
  });
  const uiStatus = mapAriaEscalationApiStatusToUi(escalation);

  return {
    id: escalation.id,
    queue,
    priority: escalation.priority ?? 'standard',
    apiStatus: escalation.status ?? null,
    source,
    reason: reason || null,
    summary: normalizedSummary.text || null,
    summaryIncidents: normalizedSummary.incidents,
    summaryRaw: normalizedSummary.raw,
    streamChannelId: escalation.streamChannelId ?? null,
    createdAt: createdAtIso,
    takenOverAt: takenOverAtIso,
    resolvedAt: resolvedAtIso,
    /** Exact listing API value — pass through to channel messages query unchanged. */
    resolvedAtRaw,
    slaDueAt: slaDueAtIso,
    isOverdue: Boolean(escalation.isOverdue),
    evidenceUrls: Array.isArray(escalation.evidenceUrls) ? escalation.evidenceUrls : [],
    resolutionMessage: escalation.resolutionMessage ?? null,
    mutedUntil: mutedUntilIso,
    partnerId: escalation.partnerId ?? null,
    eventId: escalation.eventId ?? null,
    member,
    owner: normalizeAriaEscalationOwner(escalation.owner),
    reportedUser: escalation.reportedUser
      ? normalizeAriaEscalationMember(escalation.reportedUser)
      : null,
    // UI list / room fields
    userId: member.id,
    userUid: member.uid,
    userName: member.fullName || member.email || 'Member',
    userEmail: member.email,
    avatarUrl: member.avatarUrl,
    status: uiStatus,
    ariaFlagged: String(source ?? '') === 'safety_autonomous',
    unreadCount: 0,
    memberMeta: member.email || null,
    escalationReason: preview,
    lastMessageAt: takenOverAtIso || createdAtIso,
    lastMessagePreview: preview,
    messages: [],
  };
};

export const normalizeAriaEscalationsListResponse = (payload, params = {}) => {
  const root = payload?.data ?? payload ?? {};
  const escalations = (root.escalations ?? []).map(normalizeAriaEscalation);
  const countsRoot = root.counts ?? {};
  const byQueue = { ...EMPTY_QUEUE_COUNTS, ...(countsRoot.byQueue ?? {}) };

  return {
    escalations,
    counts: {
      all: Number(countsRoot.all ?? 0),
      byQueue,
    },
    total: Number(root.total ?? escalations.length),
    page: Number(root.page ?? params.page ?? 1),
    limit: Number(root.limit ?? params.limit ?? 20),
    totalPages: Number(root.totalPages ?? 1),
  };
};

/**
 * GET /aria/escalations
 * Optional: status, queue, priority, source, resolvedToday, page, limit
 */
export const listAriaEscalations = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/aria/escalations', { params });
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to fetch Aria escalations'),
      error,
    };
  }
};

/**
 * Merge take-over API row into an existing list/room chat model.
 * Take-over payload is flatter (userId/ownerUserId) and omits messages.
 */
export const mergeAriaEscalationTakeOverIntoChat = (previousChat = {}, takeOverRow = {}) => {
  const memberFromPrevious = previousChat.member ?? {
    id: previousChat.userId ?? null,
    uid: previousChat.userUid ?? null,
    email: previousChat.userEmail ?? '',
    fullName: previousChat.userName ?? '',
    avatarUrl: previousChat.avatarUrl ?? null,
  };

  const normalized = normalizeAriaEscalation({
    ...takeOverRow,
    member: takeOverRow.member ?? {
      id: takeOverRow.userId ?? memberFromPrevious.id,
      uid: takeOverRow.member?.uid ?? memberFromPrevious.uid,
      email: takeOverRow.member?.email ?? memberFromPrevious.email,
      fullName:
        takeOverRow.member?.fullName ?? takeOverRow.member?.name ?? memberFromPrevious.fullName,
      avatarUrl: takeOverRow.member?.avatarUrl ?? memberFromPrevious.avatarUrl,
    },
    owner: takeOverRow.owner ?? {
      id: takeOverRow.ownerUserId ?? previousChat.owner?.id ?? null,
    },
    // Keep richer list fields when take-over omits them
    reason: takeOverRow.reason ?? previousChat.reason ?? null,
    summary: takeOverRow.summary ?? previousChat.summaryRaw ?? previousChat.summary ?? null,
    streamChannelId: takeOverRow.streamChannelId ?? previousChat.streamChannelId ?? null,
    queue: takeOverRow.queue ?? previousChat.queue ?? null,
    priority: takeOverRow.priority ?? previousChat.priority ?? 'standard',
    source: takeOverRow.source ?? previousChat.source ?? null,
  });

  return {
    ...previousChat,
    ...normalized,
    unreadCount: 0,
    // Room messages are loaded separately — never wipe them on take-over
    messages: previousChat.messages ?? [],
  };
};

/**
 * POST /aria/escalations/:id/take-over
 * No body required. Status becomes in_progress.
 */
export const takeOverAriaEscalation = async (escalationId) => {
  const normalizedId = String(escalationId ?? '').trim();
  if (!normalizedId) {
    return {
      success: false,
      message: 'Escalation id is required to take over.',
    };
  }

  try {
    const response = await axiosInstance.post(
      `/aria/escalations/${encodeURIComponent(normalizedId)}/take-over`,
    );
    const row = response.data?.data ?? response.data;

    return {
      success: true,
      data: row,
      message: response.data?.message ?? 'Escalation taken over',
    };
  } catch (error) {
    const httpStatus = getAriaEscalationHttpStatus(error);

    if (httpStatus === 403) {
      return {
        success: false,
        status: 403,
        message: getApiErrorMessage(
          error,
          'You cannot take over this escalation (out of scope or own member case).',
        ),
        error,
      };
    }

    if (httpStatus === 409) {
      return {
        success: false,
        status: 409,
        message: getApiErrorMessage(
          error,
          'This escalation is no longer open (already taken over).',
        ),
        error,
      };
    }

    if (httpStatus === 422) {
      return {
        success: false,
        status: 422,
        message: getApiErrorMessage(error, 'Escalation has no Stream channel.'),
        error,
      };
    }

    if (httpStatus === 404) {
      return {
        success: false,
        status: 404,
        message: getApiErrorMessage(error, 'Escalation not found.'),
        error,
      };
    }

    return {
      success: false,
      status: httpStatus,
      message: getApiErrorMessage(error, 'Failed to take over escalation.'),
      error,
    };
  }
};

/**
 * POST /aria/escalations/:id/hand-back
 * No body required. Last staff → resolved; otherwise may stay in_progress.
 */
export const handBackAriaEscalation = async (escalationId) => {
  const normalizedId = String(escalationId ?? '').trim();
  if (!normalizedId) {
    return {
      success: false,
      message: 'Escalation id is required to hand back.',
    };
  }

  try {
    const response = await axiosInstance.post(
      `/aria/escalations/${encodeURIComponent(normalizedId)}/hand-back`,
    );
    const row = response.data?.data ?? response.data;

    return {
      success: true,
      data: row,
      message: response.data?.message ?? 'Escalation handed back to Aria',
    };
  } catch (error) {
    const httpStatus = getAriaEscalationHttpStatus(error);

    if (httpStatus === 403) {
      return {
        success: false,
        status: 403,
        message: getApiErrorMessage(
          error,
          'You cannot hand back this escalation (out of scope or not the owner).',
        ),
        error,
      };
    }

    if (httpStatus === 409) {
      return {
        success: false,
        status: 409,
        message: getApiErrorMessage(
          error,
          'This escalation is not in progress (or changed while handing back).',
        ),
        error,
      };
    }

    if (httpStatus === 404) {
      return {
        success: false,
        status: 404,
        message: getApiErrorMessage(error, 'Escalation not found.'),
        error,
      };
    }

    return {
      success: false,
      status: httpStatus,
      message: getApiErrorMessage(error, 'Failed to hand escalation back to Aria.'),
      error,
    };
  }
};
